import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  EMPTY_ORDER,
  loadContextOrder,
  placeInGroup,
  reorderGroup,
  rollbackGroup,
  type ContextOrder,
} from '@/lib/context-order'
import { keys } from '@/lib/query'

/** A context's manual order (e.g. `mine/<login>`), read from the app-data branch.
 *  refetchOnWindowFocus is off: a reorder is optimistic and GitHub has
 *  read-after-write lag, so a focus refetch could briefly revert the order to a
 *  not-yet-updated blob. We trust the optimistic cache instead. */
export function useContextOrder(context: string | null) {
  return useQuery<ContextOrder>({
    queryKey: keys.contextOrder(context ?? ''),
    enabled: !!context,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: () => loadContextOrder(context!),
  })
}

/** Returns a reorder fn: optimistically place a group's issues in `orderedIds`,
 *  then persist via the server merge endpoint (which read-modify-writes the file
 *  under a lock, so concurrent reorders of different groups — even on a shared,
 *  multi-writer context — don't clobber). Rolls back on failure. */
export function useReorderContext(context: string) {
  const qc = useQueryClient()
  return async function reorder(group: string, orderedIds: string[]): Promise<void> {
    const key = keys.contextOrder(context)
    const prev = qc.getQueryData<ContextOrder>(key) ?? EMPTY_ORDER
    qc.setQueryData(key, placeInGroup(prev, group, orderedIds)) // optimistic
    try {
      await reorderGroup(context, group, orderedIds)
      // A reorder can change which issues are top-N here, which is exactly what
      // the cross-context flags read — refresh them (they show on OTHER views,
      // so there's nothing optimistic to do on this one).
      void qc.invalidateQueries({ queryKey: keys.crossContextIndex })
    } catch (err) {
      // Roll back only THIS group (merged into the latest cache) so a concurrent
      // reorder of another group of a shared context isn't clobbered. A
      // within-band reorder touches just this group, so it's exact; a cross-band
      // move's stray id self-heals on the next refetch.
      qc.setQueryData<ContextOrder>(key, (cur) => rollbackGroup(cur ?? prev, prev, group))
      toast.error('Reorder failed', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }
}
